import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../utils/theme.js";
import { api } from "../api/client.js";
import { primaryBtn, ghostBtn } from "../clients/ui.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import MeetingDocument from "./MeetingDocument.jsx";
import { computeLoe, computeTiles, countHiddenObjectives } from "./docData.js";
import { computeCrm } from "./crmBlock.js";
import { useCrmData } from "./useCrmData.js";
import { countPendingReview } from "./sectionDraft.js";
import { localToday } from "./period.js";
import { useGenerate } from "./useGenerate.js";
import { occupiedTargets } from "./applyDraft.js";
import SendPanel from "./SendPanel.jsx";

const STATUS_LABEL = { draft: "Draft", ready: "Ready", presented: "Presented", sent: "Sent" };
const NO_CHANGE_REASON = "Nothing was changed — the draft couldn’t be applied.";
const EMAIL_BEFORE_PRESENTING_BODY = "This meeting hasn’t been presented yet. You can still email the summary now.";
const SENT_UNRECORDED_MSG = "This meeting was emailed but not recorded — don’t send it again.";
// "Mon D, h:mm AM" — no existing formatter in theme.js matches this shape, so it's local to the
// send history line.
const formatSendTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

// When a Generate run writes nothing, show the most specific reason available: a section
// conflict (opens with a curly “ quote) or a Skipped: notice is more actionable than a plain
// data notice (the trimmed notice, the headline conflict, or a SharePoint/Email/Connect notice),
// so either of those wins over a data notice. Falls back to the first notice, then the generic copy.
export function pickNoChangeReason(notices) {
  if (!notices || notices.length === 0) return NO_CHANGE_REASON;
  return notices.find((n) => n.startsWith("“")) || notices.find((n) => n.startsWith("Skipped:")) || notices[0];
}

// One meeting: loads it, routes every edit through the API, and keeps the returned doc as the
// source of truth (each write returns the whole updated meeting).
export default function MeetingView({ meetingId, clientName, users, current, objectives, tasks, proposals, flash, onBack, onDeleted }) {
  const [meeting, setMeeting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPresent, setConfirmPresent] = useState(false);
  const [confirmEmailFirst, setConfirmEmailFirst] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sendInFlight, setSendInFlight] = useState(false);
  const [sentUnrecorded, setSentUnrecorded] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // A send that Graph accepted but the Ops Hub couldn't log must never look sendable again —
  // reset only when this view points at a different meeting entirely.
  useEffect(() => { setSentUnrecorded(false); }, [meetingId]);
  // Synchronous guard for a double-click on Connect, same pattern as useGenerate's runningRef.
  const connectingRef = useRef(false);
  // ClientPage recreates flash on every render; a ref keeps it out of load's deps (no reload loop).
  const flashRef = useRef(flash); flashRef.current = flash;

  const [stale, setStale] = useState(false); // a background reload failed; showing the last good copy

  // Same stale-response guard as the presenter: a focus reload can straddle a save that finishes
  // first. Only the most recently started load may apply, and none that was in flight when a write
  // landed (the write's returned meeting is authoritative).
  const loadTokenRef = useRef(0);
  const writeVersionRef = useRef(0);
  const loadedRef = useRef(false); // a meeting is on screen — later load failures stay quiet
  const adopt = (next) => { writeVersionRef.current += 1; setMeeting(next); };

  const load = useCallback(async () => {
    const token = ++loadTokenRef.current;
    const versionAtStart = writeVersionRef.current;
    try {
      const m = await api.meetings.get(meetingId);
      if (token !== loadTokenRef.current || writeVersionRef.current !== versionAtStart) return;
      loadedRef.current = true;
      setMeeting(m); setStale(false);
    } catch {
      if (token !== loadTokenRef.current || writeVersionRef.current !== versionAtStart) return;
      if (loadedRef.current) setStale(true);
      else flashRef.current("error", "Couldn’t load this meeting.");
    }
  }, [meetingId]);
  useEffect(() => { loadedRef.current = false; load(); }, [load]);
  // The presenter pop-out (or a colleague) may change the meeting; refresh when this tab regains focus.
  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // Shared result handling: success adopts the server doc; 409 w/ current = conflict;
  // 423 = sent elsewhere → reload read-only; anything else → message.
  const handle = async (res) => {
    if (res.ok) { adopt(res.data); return { ok: true }; }
    if (res.status === 409 && res.data?.current) return { ok: false, conflict: true, current: res.data.current };
    const error = res.data?.error || "Couldn’t save — try again.";
    if (res.status === 423) { flash("error", error); await load(); }
    return { ok: false, error };
  };

  const loeRows = useMemo(() => (meeting ? computeLoe(objectives, tasks, { projectId: meeting.projectId, periodStart: meeting.periodStart, periodEnd: meeting.periodEnd, hiddenObjectiveIds: meeting.loe.hiddenObjectiveIds }) : []), [meeting, objectives, tasks]);
  const tiles = useMemo(() => (meeting ? computeTiles({ tasks, objectives, proposals, sections: meeting.sections, periodStart: meeting.periodStart, periodEnd: meeting.periodEnd, today: localToday(), hiddenTiles: meeting.summary.hiddenTiles }) : []), [meeting, tasks, objectives, proposals]);

  const crmData = useCrmData(meeting?.clientId);
  const crm = useMemo(() => (meeting && crmData ? computeCrm(crmData.tracks, crmData.contacts, { periodStart: meeting.periodStart, periodEnd: meeting.periodEnd, hiddenEventIds: meeting.crm?.hiddenEventIds || [] }) : null), [meeting, crmData]);

  // onMeeting: adopt (not setMeeting) — a plain setMeeting would bypass the write-version guard
  // that protects against a stale focus-reload; see `adopt` above.
  const { generate, generating, notices, needsConnect, connect } = useGenerate({ meeting, clientName, objectives, tasks, proposals, crm, onMeeting: adopt });
  const [confirmGenerate, setConfirmGenerate] = useState(null); // string[] of sections that would be replaced
  // flashResult is false for a per-section Regenerate: its outcome is reported on the section's own
  // error line instead (see SectionBlock), so the header flash is reserved for the header button.
  const runGenerate = async (options, { flashResult = true } = {}) => {
    setConfirmGenerate(null);
    const r = await generate(options);
    if (flashResult) {
      if (!r.ok) flash("error", r.error);
      else if (r.wrote) flash("success", "Draft ready — check the items marked Review.");
      else flash("error", pickNoChangeReason(r.notices));
    }
    return r;
  };
  const startGenerate = () => { const occ = occupiedTargets(meeting); if (occ.length) setConfirmGenerate(occ); else runGenerate({ mode: "replace" }); };
  // Nothing written (a conflict on every target, or the model had nothing to add) reports as a
  // failure on the section's error line, same message the header would have flashed.
  const regenerate = async (key) => {
    const r = await runGenerate({ only: key, mode: "replace" }, { flashResult: false });
    if (r.ok && !r.wrote) return { ok: false, error: pickNoChangeReason(r.notices) };
    return r;
  };
  const canGenerate = meeting && meeting.type === "update" && meeting.status !== "sent";
  const connectM365 = async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setConnecting(true);
    try {
      const r = await connect();
      if (r.ok) flash("success", "Microsoft 365 connected — the next draft will include SharePoint and email.");
      else flash("error", "Microsoft 365 wasn’t connected.");
    } finally {
      connectingRef.current = false;
      setConnecting(false);
    }
  };

  if (!meeting) return <div style={{ padding: 24, color: colors.textMuted }}>Loading meeting…</div>;
  const editable = meeting.status !== "sent";
  const pending = countPendingReview(meeting.sections);
  // This run's notices take priority: when the generation stamp itself failed to save, the persisted
  // meeting never picked them up, so the hook's own state is the only place they're still visible.
  const displayNotices = notices.length > 0 ? notices : (meeting.generation?.notices || []);
  const openPresenter = () => {
    setConfirmPresent(false);
    const w = window.open(`${window.location.origin}/#present=${encodeURIComponent(meeting.id)}`, `present-${meeting.id}`, "popup,width=1280,height=900");
    if (!w) { flash("error", "Your browser blocked the presenter window — allow pop-ups for the Ops Hub and try again."); return; }
    // Reusing the named window doesn't raise it: bring an already-open presenter to the front.
    w.focus?.();
  };
  const present = () => (pending > 0 ? setConfirmPresent(true) : openPresenter());
  // Skip the "hasn't been presented" prompt once the meeting demonstrably has been, even if it was
  // later reopened to draft: a presented entry in statusHistory, or a prior send, both count.
  const everPresented = meeting.status === "presented" || meeting.status === "sent"
    || (meeting.statusHistory || []).some((h) => h.status === "presented")
    || (meeting.sends || []).length > 0;
  const emailMeeting = () => (everPresented ? setShowSend(true) : setConfirmEmailFirst(true));
  const setStatus = async (to) => { const r = await handle(await api.meetings.setStatus(meeting.id, to)); if (!r.ok && !r.conflict) flash("error", r.error); };
  const move = (sectionId, dir) => {
    const ids = meeting.sections.map((s) => s.id);
    const i = ids.indexOf(sectionId); const j = i + dir;
    if (j < 0 || j >= ids.length) return Promise.resolve({ ok: true });
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return api.meetings.reorder(meeting.id, ids).then(handle);
  };
  // A section 409 adopts the server's section too. An open editor keeps its draft (sections are
  // keyed by id) and still saves from the rev it started on, so it shows its conflict banner.
  const saveSection = async (sid, rev, fields) => {
    const r = await handle(await api.meetings.saveSection(meeting.id, sid, rev, fields));
    if (r.conflict) adopt((m) => (m ? { ...m, sections: m.sections.map((s) => (s.id === sid ? r.current : s)) } : m));
    return r;
  };
  // A block 409 adopts the server's block (headline/tiles/objectives are small and not drafted
  // in pieces), so the next save uses its rev instead of 409ing forever.
  const saveBlock = async (block, rev, fields) => {
    const r = await handle(await api.meetings.saveBlock(meeting.id, block, rev, fields));
    if (r.conflict) adopt((m) => (m ? { ...m, [block]: r.current } : m));
    return r;
  };
  const doDelete = async () => {
    setConfirmDelete(false);
    const res = await api.meetings.remove(meeting.id);
    if (res.ok) onDeleted(); else flash("error", res.data?.error || "Couldn’t delete this meeting.");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <button type="button" onClick={onBack} style={{ ...ghostBtn, padding: "5px 10px" }}>← All meetings</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: colors.text, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 20, padding: "4px 10px" }}>{STATUS_LABEL[meeting.status]}</span>
        {pending > 0 && <span style={{ fontSize: 12, color: colors.warning }}>{pending} item{pending === 1 ? " needs" : "s need"} review</span>}
        {stale && <span style={{ fontSize: 12, color: colors.textMuted }}>Couldn’t refresh — showing the last loaded version.</span>}
        <span style={{ flex: 1 }} />
        {canGenerate && <button type="button" onClick={startGenerate} disabled={generating} style={{ ...ghostBtn, opacity: generating ? 0.6 : 1 }}>{generating ? "Drafting… (about a minute)" : "Generate draft"}</button>}
        {canGenerate && needsConnect && <button type="button" onClick={connectM365} disabled={connecting} style={{ ...ghostBtn, opacity: connecting ? 0.6 : 1 }}>Connect Microsoft 365</button>}
        {meeting.status !== "sent" && <button type="button" onClick={present} style={primaryBtn}>Present</button>}
        {meeting.status !== "sent" && (() => {
          const emailDisabled = pending > 0 || sendInFlight || sentUnrecorded;
          const emailTitle = sentUnrecorded ? SENT_UNRECORDED_MSG : (pending > 0 ? "Resolve the items marked Review first" : undefined);
          return (
            <button type="button" onClick={emailMeeting} disabled={emailDisabled} title={emailTitle}
              style={{ ...primaryBtn, opacity: emailDisabled ? 0.5 : 1, cursor: emailDisabled ? "not-allowed" : "pointer" }}>Email meeting</button>
          );
        })()}
        {meeting.status === "draft" && <button type="button" onClick={() => setStatus("ready")} disabled={pending > 0} style={{ ...primaryBtn, opacity: pending > 0 ? 0.5 : 1, cursor: pending > 0 ? "not-allowed" : "pointer" }}>Mark ready</button>}
        {meeting.status === "ready" && <button type="button" onClick={() => setStatus("draft")} style={ghostBtn}>Back to draft</button>}
        {meeting.status === "sent" && <button type="button" onClick={() => setStatus("draft")} style={ghostBtn}>Reopen</button>}
        {meeting.status === "draft" && <button type="button" onClick={() => setConfirmDelete(true)} style={{ ...ghostBtn, color: colors.danger }}>Delete</button>}
      </div>
      {editable && displayNotices.length > 0 && (
        <div role="status" style={{ marginBottom: 10, fontSize: 13, color: colors.textMuted, display: "flex", flexDirection: "column", gap: 4 }}>
          {displayNotices.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      )}
      {sentUnrecorded && <div style={{ marginBottom: 10, fontSize: 12, color: colors.textMuted }}>{SENT_UNRECORDED_MSG}</div>}
      {meeting.status === "sent" && (meeting.sends || []).length > 0 && (
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 2 }}>
          {[...meeting.sends].reverse().slice(0, 5).map((s, i) => {
            const count = (s.to?.length || 0) + (s.cc?.length || 0);
            return (
              <div key={i} style={{ fontSize: 12, color: colors.textMuted }}>
                {`Sent ${formatSendTime(s.at)} by ${s.byName || "Someone"} to ${count} ${count === 1 ? "person" : "people"}`}
              </div>
            );
          })}
        </div>
      )}
      {showSend && meeting.status !== "sent" && (
        <SendPanel meeting={meeting} clientName={clientName} users={users} current={current} objectives={objectives} tasks={tasks} proposals={proposals} crmData={crmData} flash={flash}
          onSent={(m) => { adopt(m); setShowSend(false); }}
          onClose={() => setShowSend(false)}
          onRefresh={adopt}
          onSendingChange={setSendInFlight}
          onSentUnrecorded={() => setSentUnrecorded(true)} />
      )}
      <MeetingDocument meeting={meeting} clientName={clientName} users={users} loeRows={loeRows} tiles={tiles} crm={crm} editable={editable}
        hiddenObjectiveCount={countHiddenObjectives(objectives, { projectId: meeting.projectId, hiddenObjectiveIds: meeting.loe.hiddenObjectiveIds, tasks })}
        onSaveSection={saveSection}
        onAddSection={(title) => api.meetings.addSection(meeting.id, { title }).then(handle)}
        onRemoveSection={(sid) => api.meetings.removeSection(meeting.id, sid).then(handle)}
        onMoveSection={move}
        onSaveBlock={saveBlock}
        onToggleAgenda={(itemId, checked) => api.meetings.setAgenda(meeting.id, itemId, checked).then(handle)}
        onRegenerate={canGenerate && !generating ? regenerate : undefined} />
      {confirmDelete && (
        <ConfirmDialog title="Delete meeting?" confirmLabel="Delete meeting" onCancel={() => setConfirmDelete(false)} onConfirm={doDelete}>
          Meeting {meeting.number} and everything written in it will be deleted. This can’t be undone.
        </ConfirmDialog>
      )}
      {confirmPresent && (
        <ConfirmDialog title="Present with items still to review?" confirmLabel="Present anyway" onCancel={() => setConfirmPresent(false)} onConfirm={openPresenter}>
          {pending} item{pending === 1 ? " still needs" : "s still need"} review. Review badges never show to the client, but that text hasn’t been checked yet.
        </ConfirmDialog>
      )}
      {confirmGenerate && (
        <ConfirmDialog title="Replace existing content?" confirmLabel="Replace" onCancel={() => setConfirmGenerate(null)} onConfirm={() => runGenerate({ mode: "replace" })}>
          <div>These already have content: {confirmGenerate.join(", ")}.</div>
          <button type="button" style={{ ...ghostBtn, marginTop: 12 }} onClick={() => runGenerate({ mode: "fillEmpty" })}>Fill empty sections only</button>
        </ConfirmDialog>
      )}
      {confirmEmailFirst && (
        <ConfirmDialog title="Email before presenting?" confirmLabel="Continue" tone="primary" onCancel={() => setConfirmEmailFirst(false)} onConfirm={() => { setConfirmEmailFirst(false); setShowSend(true); }}>
          {EMAIL_BEFORE_PRESENTING_BODY}
        </ConfirmDialog>
      )}
    </div>
  );
}
